import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type Language } from '@/i18n';

/**
 * Langue active de l'interface et helper de selection bilingue.
 * Active interface language and bilingual selection helper.
 */
export function useLanguage() {
  const { i18n, t } = useTranslation();
  const language: Language = i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'fr';

  const setLanguage = useCallback(
    (next: Language) => {
      void i18n.changeLanguage(next);
    },
    [i18n],
  );

  /**
   * `pick(module, 'title')` -> `module.titleFr` ou `module.titleEn`.
   *
   * Le francais sert de repli lorsque la variante anglaise est absente,
   * ce qui evite d'afficher un libelle vide pendant la traduction d'un contenu.
   */
  const pick = useCallback(
    <T extends object>(source: T | null | undefined, key: string): string => {
      if (!source) return '';
      const record = source as Record<string, unknown>;
      const value = record[language === 'en' ? `${key}En` : `${key}Fr`];
      if (typeof value === 'string' && value.length > 0) return value;
      const fallback = record[`${key}Fr`];
      return typeof fallback === 'string' ? fallback : '';
    },
    [language],
  );

  return { language, setLanguage, pick, t };
}
